import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * SeoHead – zentrale SEO-Komponente für alle Seiten der smartRATINGS Homepage.
 *
 * Setzt pro Seite:
 *  - <title>
 *  - <meta name="description">
 *  - <link rel="canonical">
 *  - hreflang für de-DE und de-AT (x-default = de-DE)
 *  - noindex für rechtliche Seiten (Impressum, Datenschutz, AGB)
 *
 * Props:
 *  - title        {string}  Seitentitel (ohne " | smartRATINGS" – wird automatisch ergänzt)
 *  - description  {string}  Meta-Description
 *  - noindex      {bool}    true für rechtliche Seiten
 *  - atSlug       {string}  Optionaler abweichender AT-Pfad (Standard: gleicher Pfad unter /de-at/)
 */
const BASE_URL = 'https://smartratings.ai';
const AT_BASE  = 'https://smartratings.ai/de-at';

const SeoHead = ({ title, description, noindex = false, atSlug = null }) => {
  const location  = useLocation();
  const path      = location.pathname.replace(/\/$/, '') || '/';
  const canonical = `${BASE_URL}${path === '/' ? '' : path}/`;

  // AT-URL: entweder explizit angegeben oder automatisch aus aktuellem Pfad
  const atPath    = atSlug
    ? `${AT_BASE}${atSlug}/`
    : `${AT_BASE}${path === '/' ? '' : path}/`;

  const fullTitle = title
    ? `${title} | smartRATINGS`
    : 'smartRATINGS – Professionelles Bewertungsmanagement';

  return (
    <Helmet>
      <html lang="de-DE" />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}

      {/* Robots */}
      {noindex
        ? <meta name="robots" content="noindex, follow" />
        : <meta name="robots" content="index, follow" />
      }

      {/* Canonical */}
      <link rel="canonical" href={canonical} />

      {/* hreflang – nur für indexierbare Seiten */}
      {!noindex && (
        <>
          <link rel="alternate" hrefLang="de-DE" href={canonical} />
          <link rel="alternate" hrefLang="de-AT" href={atPath} />
          <link rel="alternate" hrefLang="x-default" href={canonical} />
        </>
      )}
    </Helmet>
  );
};

export default SeoHead;
